import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useAB } from 'Douleutaras/app/lib/ab';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { getCountryCode } from 'Douleutaras/hooks/localization';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { selectCurrentUser } from 'Douleutaras/redux/data/auth/authSlice';
import { jobSelectors } from 'Douleutaras/redux/data/jobs/jobsSlice';
import { categoriesSelector } from 'Douleutaras/redux/data/categories/categoriesSlice';
import { businessUnitsSelector } from 'Douleutaras/redux/data/businessUnits/businessUnitsSlice';
import { jobMunicipalitySelectors } from 'Douleutaras/redux/data/jobMunicipality/jobMunicipalitySlice';
import { BusinessModel, JobPricingModels } from 'Douleutaras/types';
import { addSnackbarNotification, SnackbarNotificationType } from 'Douleutaras/redux/data/ui/snackbarSlice';
import { PhoneNumberRevealedSource } from 'Douleutaras/app/lib/posthog/posthog.types';

import './PhoneDropdown.scss';

interface PhoneDropdownProps {
  phone: string;
  jobId: string;
  bidId: string;
  sellerId: string;
  source: PhoneNumberRevealedSource;
  children: React.ReactElement<{ onClick?(): void; }>;
  onReveal?(): void;
}

export const PhoneDropdown = ({ phone, jobId, bidId, sellerId, source, children, onReveal }: PhoneDropdownProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery({ maxWidth: TABLET_BIG.MAX_WIDTH - 1 });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(undefined);

  // Phone Number Revealed event tracking
  const { captureProCallIntent } = useAB();
  const user = useAppSelector(selectCurrentUser);
  const job = useAppSelector(state => jobSelectors.selectById(state, jobId));
  const category = useAppSelector(state => categoriesSelector.selectById(state, job?.relationships?.category?.data?.id));
  const businessUnit = useAppSelector(state => businessUnitsSelector.selectById(state, category?.relationships?.businessUnit?.data?.id));
  const municipality = useAppSelector(state => jobMunicipalitySelectors.selectById(state, job?.relationships?.municipality?.data?.id));
  const postingFlow = useAppSelector(state => state.pros.postingFlow);
  const countryCode = getCountryCode();

  const fireRevealEvent = () => {
    const isCleaning = parseInt(job?.relationships?.category?.data?.id, 0) === COUNTRY_CONFIG.CLEANING_CATEGORY[countryCode];
    const businessModel = job?.attributes?.pricingModel === JobPricingModels.MONEY_MODEL ? BusinessModel.MONEY : BusinessModel.CREDIT;

    // @AB PostHog event
    captureProCallIntent({
      category_slug: category?.attributes?.slug,
      sub_category_slug: job?.attributes?.subCategorySlug,
      business_unit_slug: businessUnit?.attributes?.slug,
      business_model: businessModel,
      municipality_slug: municipality?.attributes?.slug,
      county_slug: municipality?.attributes?.county.slug,
      job_posting_flow: postingFlow?.posting_flow,
      is_cleaning: isCleaning,
      is_b2b: job?.attributes?.isB2b ?? false,
      is_core: (!isCleaning && job?.attributes?.isB2b) ?? false,
      job_id: jobId,
      bid_id: bidId,
      pro_id: sellerId,
      customer_id: `${user?.id}`,
      source_phone_reveal: source
    });
  };

  const handleToggle = () => {
    if (isMobile) {
      fireRevealEvent();
      onReveal?.();
      window.location.href = `tel:${phone}`;

      return;
    }

    if (!open) {
      fireRevealEvent();
      onReveal?.();
    }
    setOpen(prev => !prev);
  };

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(phone);
    setOpen(false);
    dispatch(addSnackbarNotification({
      type: SnackbarNotificationType.SUCCESS,
      message: t('T.PHONE-DROPDOWN.PHONE-COPIED')
    }));
  }, [phone]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div styleName="phone-dropdown-wrapper" ref={ref}>
      {React.cloneElement(children, { onClick: handleToggle })}
      {open &&
        <div styleName="phone-dropdown">
          <a
            href={`tel:${phone}`}
            styleName="phone-dropdown__option"
            onClick={() => setOpen(false)}
          >
            {t('T.PHONE-DROPDOWN.CALL-{{phone}}', { phone })}
          </a>
          <button
            type="button"
            styleName="phone-dropdown__option phone-dropdown__option--copy"
            onClick={handleCopy}
          >
            {t('T.PHONE-DROPDOWN.COPY-PHONE')}
          </button>
        </div>
      }
    </div>
  );
};
